import { Paginated } from "@/@types/generics/paginated";
import { appApi } from "../api";
import { Role } from "@/@types/app/Role";
import { PaginationFilter } from "@/@types/generics/genericals";
import { UserAccount } from "@/@types/app/UserAccount";
import { CityLicense } from "@/@types/app/CityLicense";
import { ModuleEnum } from "@/utils/enums/ModuleEnum";
import { Module } from "@/@types/app/Module";
import { ResumeGrouped } from "../esus/attendanceService";
import { HealthUnit } from "@/@types/esus/HealthUnit";
import { Account } from "@/@types/app/Account";
import { FederalUnit } from "@/@types/app/FederalUnit";

export type UsersAccountAccess = {
	userId: number;
	fullName: string;
	identifier: string;
	email: string;
	phone: string;
	occupation: string;
	gender: string;
	birthdate: string;
	federalUnitAbreviattion: string;
	cityDescription: string;
	street: string;
	houseNumber: string;
	neighborhood: string;
	isAdmin: boolean;
	isLocked: boolean;
	hasProfileActive: boolean;
	role: Role;
	accessByCNES?: ResumeGrouped<HealthUnit, object>[];
	accessByCNESRequested?: ResumeGrouped<HealthUnit, object>[];
	accessByMunicipality?: ResumeGrouped<Account, object>[];
	accessByMunicipalityRequested?: ResumeGrouped<Account, object>[];
	accessByUF?: ResumeGrouped<FederalUnit, object>[];
	accessByUfRequested?: ResumeGrouped<FederalUnit, object>[];
	modules?: Module[];
};

export type UserAccountsUnitsAccess = {
	userIdentifier: string;
	userName: string;
	ibgeCode: string;
	healthUnitName: string;
	isActive: boolean;
	cnes: string;
	role: Role;
};

export type UpdateAccessUserAccountParams = {
	userId: number;
	uf: string;
	ibgeCode: string;
	cnesList: string[];
	roleId?: string;
	module?: number;
};

type CurrentAccount = {
	userAccount?: UserAccount;
	permissions?: string[];
	currentCityLicense?: CityLicense;
	currentModule?: Module;
	isAuthenticated?: boolean;
};

type GetUsersAccountParams = PaginationFilter & {
	uf?: string;
	ibgeCode?: string;
	filterByUf: string;
	filterByIbgeCode: string;
	filterByCnes: string;
	fullName?: string;
	isLocked: boolean | null;
	module?: ModuleEnum | null;
	filterByModule?: ModuleEnum | null;
	hasProfileActive: boolean | null;
};

type RevokeAccessUserAccountParams = {
	uf?: string;
	ibgeCode?: string;
	userId: number;
};

export async function getUserAccounts(
	uf?: string,
	ibgeCode?: string,
	identifier?: string,
	cnes?: string,
	module?: ModuleEnum
) {
	const { data } = await appApi.get<UserAccountsUnitsAccess[]>(
		`${uf ? `/uf/${uf}` : ""}${
			uf && ibgeCode ? `/city/${ibgeCode}` : ""
		}/useraccount/units-access/`,
		{
			identifier,
			cnes,
			module,
		}
	);
	return data || [];
}

export async function getCurrentUserAccount(
	uf?: string,
	ibgeCode?: string,
	cnes?: string,
	moduleId?: ModuleEnum
) {
	const response = await appApi.get<CurrentAccount>(
		`${uf ? `/uf/${uf}` : ""}${uf && ibgeCode ? `/city/${ibgeCode}` : ""}/useraccount/current`,
		{
			cnes,
			moduleId,
		}
	);
	return { ...response.data, isAuthenticated: response.status === 200 };
}

export async function updateAccessUserAccount({
	uf,
	ibgeCode,
	...params
}: UpdateAccessUserAccountParams) {
	const response = await appApi.post(
		`${uf ? `/uf/${uf}` : ""}${
			uf && ibgeCode ? `/city/${ibgeCode}` : ""
		}/useraccount/update-access`,
		params
	);
	return response.status === 200;
}

export async function getUsersAccount({ uf, ibgeCode, ...params }: GetUsersAccountParams) {
	const { data } = await appApi.get<Paginated<UsersAccountAccess>>(
		`${uf ? `/uf/${uf}` : ""}${
			uf && ibgeCode ? `/city/${ibgeCode}` : ""
		}/useraccount/users-account`,
		params
	);
	return data || ({} as Paginated<UsersAccountAccess>);
}

export async function revokeAccessUserAccount({
	uf,
	ibgeCode,
	...params
}: RevokeAccessUserAccountParams) {
	const response = await appApi.post(
		`${uf ? `/uf/${uf}` : ""}${
			uf && ibgeCode ? `/city/${ibgeCode}` : ""
		}/useraccount/revoke-access`,
		params
	);
	return response.status === 200;
}
